import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { analyticsActions } from '@actions';
import { subtractYears } from '@helpers/dates';
import { ProfileName } from '@constants/integrations';
import { ANALYTICS_PLATFORMS } from '@constants/social-posts';
import { GroupByFilter } from '@constants/analytics';
import { useSocialPostsPlatforms, useLocations } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import FiltersPanel from './FiltersPanel';
import SocialPostsTable from './SocialPostsTable';
import SocialRates from './SocialRates';
import SocialPageTrend from './SocialPageTrend';
import SocialEngagementsTrend from './SocialEngagementsTrend';

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const TrendsRow = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '100%',
    },
    '@media print': {
        gridTemplateColumns: '100%',
    },
    '& > *': {
        marginBottom: 0,
    },
    marginBottom: 16,
});

const SocialPosts = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        platforms: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { socialPostsData, socialPostsLoading } = useSelector(state => state.analytics);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const { getConnectedPlatforms } = useSocialPostsPlatforms();

    const availableLocations = useMemo(
        () => locations.filter(l => l.profiles?.some(p => p.type === ProfileName.FacebookOfficial)),
        [locations],
    );
    const availableLocationsIds = useMemo(() => availableLocations.map(l => l.companyId), [availableLocations]);
    const platformsConnected = useMemo(
        () => getConnectedPlatforms(availableLocationsIds).filter(p => ANALYTICS_PLATFORMS.includes(p)),
        [availableLocationsIds],
    );

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            locations: mapLocationsToFormValue(availableLocations),
            platforms: platformsConnected,
        }));
    }, [availableLocations, platformsConnected, dispatch]);

    useEffect(() => {
        if (filter.locations.length && filter.platforms.length && firstRender.current) {
            onApplyFilters();
            firstRender.current = false;
        }
    }, [filter, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsSocialPosts({
                locations: filter.locations.map(l => l.id),
                platforms: filter.platforms,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
            }),
        );
    };

    return (
        <StyledAnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="social-posts"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-social-posts">
                <SocialRates data={socialPostsData.rates} />
                <TrendsRow>
                    <SocialPageTrend
                        title="AnalyticsSocialPosts.reach28"
                        wrapperId="reach28-chart-wrapper"
                        data={socialPostsData.reach28TrendData}
                        language={language}
                        grouping={filter.grouping}
                    />
                    <SocialPageTrend
                        title="AnalyticsSocialPosts.followers"
                        wrapperId="followers-chart-wrapper"
                        data={socialPostsData.followersTrendData}
                        language={language}
                        grouping={filter.grouping}
                    />
                </TrendsRow>
                <SocialEngagementsTrend
                    data={socialPostsData.engagementsTrendData}
                    language={language}
                    grouping={filter.grouping}
                />
                <SocialPostsTable data={socialPostsData.tableData} isLoading={socialPostsLoading} filter={filter} />
            </AnalyticsContainer>
        </StyledAnalyticsWrapper>
    );
});

SocialPosts.displayName = 'SocialPosts';

export default SocialPosts;
